/**
 * Investigation Service
 * 
 * Handles investigation API operations for looking up users, devices, risk data,
 * secure score, advanced hunting, and security posture.
 * Connects to func-axn-analyst-api via SWA linked backend.
 * 
 * API endpoints:
 * - GET /api/investigate/:tenantId/user/:identifier
 * - GET /api/investigate/:tenantId/device/:deviceId
 * - GET /api/investigate/:tenantId/risky-users
 * - GET /api/investigate/:tenantId/secure-score
 * - GET /api/investigate/:tenantId/security-posture
 * - POST /api/investigate/:tenantId/hunt
 * - GET /api/investigate/:tenantId/hunt/presets
 */

import type {
  UserInvestigationResult,
  DeviceInvestigationResult,
  RiskyUsersResult,
  RiskLevel,
  SecureScoreResult,
  SecurityPostureResult,
  HuntingQueryResult,
  HuntingPresetsResponse,
} from '../data/types';
import { apiFetch, type ApiResponse } from './api';

// ============================================================================
// USER INVESTIGATION
// ============================================================================

/**
 * Investigate a user by UPN or ID
 * Returns user profile, risk data, and sign-in history
 */
export async function investigateUser(
  tenantId: string,
  identifier: string
): Promise<ApiResponse<UserInvestigationResult>> {
  const result = await apiFetch<UserInvestigationResult>(
    `/investigate/${encodeURIComponent(tenantId)}/user/${encodeURIComponent(identifier)}`
  );

  if (!result.success) {
    return { data: null as unknown as UserInvestigationResult, success: false, error: result.error };
  }

  return { data: result.data, success: true };
}

// ============================================================================
// DEVICE INVESTIGATION
// ============================================================================

/**
 * Investigate a device by ID
 * Returns device details including compliance and management status
 */
export async function investigateDevice(
  tenantId: string,
  deviceId: string
): Promise<ApiResponse<DeviceInvestigationResult>> {
  const result = await apiFetch<DeviceInvestigationResult>(
    `/investigate/${encodeURIComponent(tenantId)}/device/${encodeURIComponent(deviceId)}`
  );

  if (!result.success) {
    return { data: null as unknown as DeviceInvestigationResult, success: false, error: result.error };
  }

  return { data: result.data, success: true };
}

// ============================================================================
// RISKY USERS
// ============================================================================

/**
 * Get risky users for a tenant
 */
export async function getRiskyUsers(
  tenantId: string,
  options?: { top?: number; riskLevel?: RiskLevel }
): Promise<ApiResponse<RiskyUsersResult>> {
  const params = new URLSearchParams();
  if (options?.top) {
    params.set('top', options.top.toString());
  }
  if (options?.riskLevel) {
    params.set('riskLevel', options.riskLevel);
  }

  const queryString = params.toString();
  const url = `/investigate/${encodeURIComponent(tenantId)}/risky-users${queryString ? `?${queryString}` : ''}`;

  const result = await apiFetch<RiskyUsersResult>(url);

  if (!result.success) {
    return { data: null as unknown as RiskyUsersResult, success: false, error: result.error };
  }

  return { data: result.data, success: true };
}

// ============================================================================
// SECURE SCORE
// ============================================================================

/**
 * Get secure score for a tenant
 * Includes current score, history, and recommendations
 */
export async function getSecureScore(
  tenantId: string
): Promise<ApiResponse<SecureScoreResult>> {
  const result = await apiFetch<SecureScoreResult>(
    `/investigate/${encodeURIComponent(tenantId)}/secure-score`
  );

  if (!result.success) {
    return { data: null as unknown as SecureScoreResult, success: false, error: result.error };
  }

  return { data: result.data, success: true };
}

// ============================================================================
// SECURITY POSTURE
// ============================================================================

/**
 * Get overall security posture for a tenant
 * Includes secure score, risky users, risky service principals, and threat indicators
 */
export async function getSecurityPosture(
  tenantId: string
): Promise<ApiResponse<SecurityPostureResult>> {
  const result = await apiFetch<SecurityPostureResult>(
    `/investigate/${encodeURIComponent(tenantId)}/security-posture`
  );

  if (!result.success) {
    return { data: null as unknown as SecurityPostureResult, success: false, error: result.error };
  }

  return { data: result.data, success: true };
}

// ============================================================================
// ADVANCED HUNTING
// ============================================================================

/**
 * Run a custom KQL query
 */
export async function runHuntingQuery(
  tenantId: string,
  query: string
): Promise<ApiResponse<HuntingQueryResult>> {
  const result = await apiFetch<HuntingQueryResult>(
    `/investigate/${encodeURIComponent(tenantId)}/hunt`,
    {
      method: 'POST',
      body: JSON.stringify({ query }),
    }
  );

  if (!result.success) {
    return { data: null as unknown as HuntingQueryResult, success: false, error: result.error };
  }

  return { data: result.data, success: true };
}

/**
 * Run an entity-specific hunting query
 * @param tenantId - The tenant ID
 * @param preset - The preset query ID (e.g., 'user-signins', 'device-processes')
 * @param entityType - The type of entity ('user' | 'device' | 'ip' | 'email')
 * @param entityValue - The entity value to search for (e.g., user email, device name)
 */
export async function runEntityHuntingQuery(
  tenantId: string,
  preset: string,
  entityType: 'user' | 'device' | 'ip' | 'email',
  entityValue: string
): Promise<ApiResponse<HuntingQueryResult>> {
  const result = await apiFetch<HuntingQueryResult>(
    `/investigate/${encodeURIComponent(tenantId)}/hunt`,
    {
      method: 'POST',
      body: JSON.stringify({ preset, entityType, entityValue }),
    }
  );

  if (!result.success) {
    return { data: null as unknown as HuntingQueryResult, success: false, error: result.error };
  }

  return { data: result.data, success: true };
}

/**
 * Run a global hunting query (not entity-specific)
 */
export async function runGlobalHuntingQuery(
  tenantId: string,
  preset: string
): Promise<ApiResponse<HuntingQueryResult>> {
  const result = await apiFetch<HuntingQueryResult>(
    `/investigate/${encodeURIComponent(tenantId)}/hunt`,
    {
      method: 'POST',
      body: JSON.stringify({ preset }),
    }
  );

  if (!result.success) {
    return { data: null as unknown as HuntingQueryResult, success: false, error: result.error };
  }

  return { data: result.data, success: true };
}

/**
 * Get available hunting presets organized by entity type
 */
export async function getHuntingPresets(
  tenantId: string
): Promise<ApiResponse<HuntingPresetsResponse>> {
  const result = await apiFetch<HuntingPresetsResponse>(
    `/investigate/${encodeURIComponent(tenantId)}/hunt/presets`
  );

  if (!result.success) {
    return {
      data: { entityPresets: {}, globalPresets: [] },
      success: false,
      error: result.error
    };
  }

  return { data: result.data, success: true };
}

/**
 * Get the cached capability snapshot for a tenant. Drives the capability
 * strip on the Custom KQL page + the empty-state hint logic. Returns
 * `capabilities: null` when no snapshot exists yet (caller should fail
 * open and skip the strip).
 */
export interface TenantCapabilitiesResponse {
  tenantId: string;
  capabilities: {
    mde: boolean;
    mdo: boolean;
    mdca: boolean;
    mdi: boolean;
    entraP1: boolean;
    entraP2: boolean;
    purviewIrm: boolean;
    defenderXdrBaseline: boolean;
  } | null;
  lastRefreshedAt: string | null;
}

export async function getTenantCapabilities(
  tenantId: string
): Promise<ApiResponse<TenantCapabilitiesResponse>> {
  const result = await apiFetch<TenantCapabilitiesResponse>(
    `/investigate/${encodeURIComponent(tenantId)}/capabilities`
  );

  if (!result.success) {
    return {
      data: { tenantId, capabilities: null, lastRefreshedAt: null },
      success: false,
      error: result.error,
    };
  }

  return { data: result.data, success: true };
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get display color for risk level
 */
export function getRiskLevelColor(level: RiskLevel | null | undefined): string {
  switch (level) {
    case 'high':
      return '#dc2626'; // red-600
    case 'medium':
      return '#d97706'; // amber-600
    case 'low':
      return '#2563eb'; // blue-600
    case 'none':
      return '#16a34a'; // green-600
    default:
      return '#6b7280'; // gray-500
  }
}

/**
 * Get display label for risk level
 */
export function getRiskLevelLabel(level: RiskLevel | null | undefined): string {
  switch (level) {
    case 'high':
      return 'High Risk';
    case 'medium':
      return 'Medium Risk';
    case 'low':
      return 'Low Risk';
    case 'none':
      return 'No Risk';
    case 'hidden':
      return 'Hidden';
    default:
      return 'Unknown';
  }
}

/**
 * Format location for display
 */
export function formatLocation(location: { city: string | null; state: string | null; countryOrRegion: string | null }): string {
  const parts = [location.city, location.state, location.countryOrRegion].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'Unknown';
}

/**
 * Get sign-in status display info
 */
export function getSignInStatusInfo(errorCode: number): { label: string; isSuccess: boolean } {
  if (errorCode === 0) {
    return { label: 'Success', isSuccess: true };
  }
  return { label: `Failed (${errorCode})`, isSuccess: false };
}
